export const isInventoryShoesProduct = (product = {}) => {
  if (Number(product.category_id) === 2) return true

  return String(product.category_name || '').trim().toLowerCase() === 'shoes'
}

export const normalizeInventoryShoeSizeInput = (value) => {
  const rawValue = value == null ? '' : String(value).trim()

  if (!rawValue) return ''

  const normalizedValue = rawValue.replace(',', '.')

  if (!/^\d+(?:\.\d+)?$/.test(normalizedValue)) return ''

  const numericValue = Number(normalizedValue)

  if (!Number.isFinite(numericValue)) return ''

  return String(numericValue)
}

export const normalizeInventoryProductShoeSizeInput = (value) => {
  const normalizedValue = normalizeInventoryShoeSizeInput(value)
  const size = Number(normalizedValue)

  return normalizedValue && size > 0 && size <= 50 ? normalizedValue : ''
}

export const normalizeInventoryInsoleSizeInput = (value) => {
  const normalizedValue = normalizeInventoryShoeSizeInput(value)

  if (!normalizedValue) return ''

  return Number(normalizedValue) > 0
    ? normalizedValue
    : ''
}

export const resolveInventoryBulkPhotoKindOptions = (
  product = {},
  baseOptions = []
) => {
  if (!isInventoryShoesProduct(product)) return baseOptions

  const insoleOption = {
    value: 'insole_photo',
    label: 'Insole View'
  }

  const options = baseOptions.filter(
    (option) => option.value !== 'insole_photo'
  )

  const photoDetailsIndex = options.findIndex(
    (option) => option.value === 'photo_details'
  )

  if (photoDetailsIndex === -1) {
    return [
      ...options,
      insoleOption
    ]
  }

  return [
    ...options.slice(0, photoDetailsIndex),
    insoleOption,
    ...options.slice(photoDetailsIndex)
  ]
}

export const resolveInventoryBulkPhotoFields = (
  product = {},
  baseFields = []
) => [
  ...baseFields,
  ...getInventoryShoesBulkPhotoFields(product)
]

export const getInventoryShoesBulkPhotoFields = (product = {}) => {
  if (!isInventoryShoesProduct(product)) return []

  return [
    {
      name: 'insole_photo',
      label: 'Insole View'
    }
  ]
}

export const validateInventoryShoesRequiredFields = (
  product = {},
  values = {},
  {
    validateProductSize = true,
    validateInsoleSize = true,
    validateOnlyChanged = false
  } = {}
) => {
  if (!isInventoryShoesProduct(product)) return ''

  const isFieldChanged = (fieldName) => {
    const originalValue = product[fieldName] == null
      ? ''
      : String(product[fieldName]).trim()

    const currentValue = values[fieldName] == null
      ? ''
      : String(values[fieldName]).trim()

    return originalValue !== currentValue
  }

  const shouldValidateProductSize =
    validateProductSize &&
    (!validateOnlyChanged || isFieldChanged('p_size'))

  const shouldValidateInsoleSize =
    validateInsoleSize &&
    (!validateOnlyChanged || isFieldChanged('insole_size'))

  if (
    shouldValidateProductSize &&
    !normalizeInventoryProductShoeSizeInput(values.p_size)
  ) {
    return 'Product Size wajib diisi dengan angka lebih dari 0 dan maksimal 50 untuk kategori Shoes.'
  }

  if (
    shouldValidateInsoleSize &&
    String(values.insole_size == null ? '' : values.insole_size).trim() !== '' &&
    !normalizeInventoryInsoleSizeInput(values.insole_size)
  ) {
    return 'Insole Size harus berupa angka lebih dari 0 jika diisi untuk kategori Shoes.'
  }

  return ''
}

export const resolveInventoryShoesMissingRequirementTab = (
  missingRequirements = []
) => (
  missingRequirements.some(
    (label) => [ 'Product Size', 'Insole Size' ].includes(label)
  )
    ? 'product'
    : 'photos'
)

export const getInventoryShoesMissingRequiredWarnings = (
  product = {},
  values = {},
  { hasInsolePhoto = false } = {}
) => {
  if (!isInventoryShoesProduct(product)) return []

  const missing = []

  if (!normalizeInventoryProductShoeSizeInput(values.p_size)) {
    missing.push('Product Size')
  }

  if (!hasInsolePhoto) {
    missing.push('Insole View')
  }

  return missing
}

export const buildInventoryShoesSizingPatch = (product = {}, values = {}) => {
  if (!isInventoryShoesProduct(product)) {
    return {
      p_size: values.p_size
    }
  }

  const rawProductSize = values.p_size == null
    ? ''
    : String(values.p_size).trim()

  const normalizedProductSize = normalizeInventoryProductShoeSizeInput(rawProductSize)

  const patch = {
    p_size: normalizedProductSize || rawProductSize
  }

  const rawInsoleSize = values.insole_size == null
    ? ''
    : String(values.insole_size).trim()

  if (!rawInsoleSize) {
    patch.insole_size = ''
    return patch
  }

  const normalizedInsoleSize = normalizeInventoryInsoleSizeInput(rawInsoleSize)

  if (normalizedInsoleSize) {
    patch.insole_size = normalizedInsoleSize
  }

  return patch
}

export const prepareInventoryShoesUpdatePayload = (
  product = {},
  values = {},
  editablePayload = {}
) => {
  const payload = { ...editablePayload }

  if (!isInventoryShoesProduct(product)) {
    delete payload.insole_size
    delete payload.insole_photo

    return payload
  }

  const sizingPatch = buildInventoryShoesSizingPatch(product, values)

  if (Object.prototype.hasOwnProperty.call(payload, 'p_size')) {
    payload.p_size = sizingPatch.p_size
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'insole_size')) {
    if (Object.prototype.hasOwnProperty.call(sizingPatch, 'insole_size')) {
      payload.insole_size = sizingPatch.insole_size
    } else {
      delete payload.insole_size
    }
  }

  return payload
}

export const getInventoryShoesDetailData = (product = {}) => {
  const isShoes = isInventoryShoesProduct(product)

  if (!isShoes) {
    return {
      isShoes        : false,
      insoleSize     : null,
      insolePhotoUrl : null
    }
  }

  const details = product.details && typeof product.details === 'object'
    ? product.details
    : {}

  const images = product.images && typeof product.images === 'object'
    ? product.images
    : {}

  const firstPresent = (...values) => (
    values.find((value) => value !== undefined && value !== null && value !== '')
  )

  return {
    isShoes        : true,
    insoleSize     : firstPresent(
      product.insole_size,
      details.insole_size
    ) ?? null,
    insolePhotoUrl : firstPresent(
      product.insole_photo_url,
      images.insole_photo_url,
      details.insole_photo_url
    ) ?? null
  }
}
